"use client"

/**
 * Type-safe PostHog event tracking with PII protection.
 *
 * Usage:
 *   import { track } from "@/shared/lib/analytics"
 *   track("auth:signup_start", { method: "email" })
 *
 * PII is automatically stripped at runtime as a safety net.
 * TypeScript enforces correct event names and property types at compile time.
 */

import type { AnalyticsEvent, EventPropertyMap } from "./events"
import { handleActivationSourceEvent } from "./activation"
import { isPostHogTelemetryEnabled } from "../monitoring/telemetry-config"

// ── PII Protection ─────────────────────────────────────────────────
// Fields that must NEVER be sent to PostHog (runtime safety net)

const PII_BLOCKLIST = new Set([
  "email",
  "password",
  "card",
  "card_number",
  "credit_card",
  "cvv",
  "ssn",
  "address",
  "phone",
  "phone_number",
  "ip",
  "ip_address",
  "secret",
  "token",
])

function scrubPII(props: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(props)) {
    if (PII_BLOCKLIST.has(key.toLowerCase())) continue
    cleaned[key] = value
  }
  return cleaned
}

// ── PostHog Access ─────────────────────────────────────────────────

interface PostHogInstance {
  capture: (event: string, properties?: Record<string, unknown>) => void
  identify?: (
    distinctId: string,
    properties?: Record<string, unknown>
  ) => void
  register: (properties: Record<string, unknown>) => void
}

function getPostHog(): PostHogInstance | null {
  if (!isPostHogTelemetryEnabled()) return null
  if (typeof window === "undefined") return null
  const ph = (window as unknown as { posthog?: PostHogInstance }).posthog
  if (!ph || typeof ph.capture !== "function") return null
  return ph
}

// ── Typed Track Function ───────────────────────────────────────────

const isDev = process.env.NODE_ENV === "development"

/**
 * Track a PostHog event with full type safety.
 *
 * Event names and properties are enforced by TypeScript via EventPropertyMap.
 * PII is automatically stripped at runtime.
 *
 * @example
 * track("auth:signup_start", { method: "email", source: "modal" })
 * track("chat:message_send", { character_id: "abc", conversation_id: "xyz", is_image_intent: false, message_length: 42 })
 */
export function track<E extends AnalyticsEvent>(
  event: E,
  properties: EventPropertyMap[E]
): void {
  const ph = getPostHog()
  if (!ph) return

  const cleaned = scrubPII(properties as Record<string, unknown>)

  if (isDev) {
    console.log(
      `%c[analytics] ${event}`,
      "color: #87CEFA; font-weight: bold;",
      cleaned
    )
  }

  ph.capture(event, cleaned)
  handleActivationSourceEvent(ph, event, cleaned as EventPropertyMap[E])
}

/**
 * Register super properties that attach to every subsequent event.
 * Use after login/subscription changes.
 */
export function registerSuperProperties(
  properties: Record<string, unknown>
): void {
  const ph = getPostHog()
  if (!ph) return

  const cleaned = scrubPII(properties)
  ph.register(cleaned)
}
